import { useMemo, useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import FilterBar, {
  formatDateInputValue,
} from "../../components/common/FilterBar";
import Checkbox from "../../components/common/Checkbox";
import ActionButton from "../../components/common/ActionButton";
import { useStore } from "../../context/StoreContext";
import {
  useDeleteDayOverrideMutation,
  useGetDayOverridesQuery,
  useUpsertDayOverrideMutation,
} from "../../store/api";

const DAY_OPTIONS = [
  { value: "0", label: "Sunday (0)" },
  { value: "1", label: "Monday (1)" },
  { value: "2", label: "Tuesday (2)" },
  { value: "3", label: "Wednesday (3)" },
  { value: "4", label: "Thursday (4)" },
  { value: "5", label: "Friday (5)" },
  { value: "6", label: "Saturday (6)" },
];

const DAY_NAME_BY_VALUE = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

const toDateString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value instanceof Date) return formatDateInputValue(value);
  return "";
};

const DayOverrides = () => {
  const { selectedStoreId } = useStore();
  const today = formatDateInputValue(new Date());

  const [draftFilters, setDraftFilters] = useState({
    from: today,
    to: today,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    from: today,
    to: today,
  });

  const [showForm, setShowForm] = useState(false);
  const [editingTargetDate, setEditingTargetDate] = useState("");
  const [form, setForm] = useState({
    targetDate: today,
    effectiveDayOfWeek: "0",
    isActive: true,
    reason: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    targetDate: "",
  });

  const queryArgs = useMemo(
    () => ({
      storeId: selectedStoreId,
      from: appliedFilters.from || undefined,
      to: appliedFilters.to || undefined,
    }),
    [selectedStoreId, appliedFilters],
  );

  const { data, isLoading, isFetching, isError, error } = useGetDayOverridesQuery(
    queryArgs,
    {
      skip: !selectedStoreId,
    },
  );

  const [upsertDayOverride, upsertState] = useUpsertDayOverrideMutation();
  const [deleteDayOverride, deleteState] = useDeleteDayOverrideMutation();

  const overrides = Array.isArray(data) ? data : [];

  const setFilterAndApply = (next) => {
    setDraftFilters(next);
    setAppliedFilters(next);
  };

  const resetMutationFeedback = () => {
    setSuccessMessage("");
    if (typeof upsertState.reset === "function") {
      upsertState.reset();
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    resetMutationFeedback();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedStoreId) return;
    resetMutationFeedback();
    try {
      await upsertDayOverride({
        storeId: selectedStoreId,
        targetDate: form.targetDate,
        payload: {
          effectiveDayOfWeek: Number(form.effectiveDayOfWeek),
          isActive: Boolean(form.isActive),
          reason: form.reason?.trim() || null,
        },
      }).unwrap();
      setSuccessMessage("Day override saved successfully.");
      setShowForm(false);
      setEditingTargetDate("");
      setForm({
        targetDate: today,
        effectiveDayOfWeek: "0",
        isActive: true,
        reason: "",
      });
    } catch (err) {
      // handled by UI state
    }
  };

  const handleEdit = (row) => {
    setShowForm(true);
    setEditingTargetDate(row.targetDate || "");
    setForm({
      targetDate: row.targetDate || today,
      effectiveDayOfWeek: String(row.effectiveDayOfWeek ?? 0),
      isActive: Boolean(row.isActive),
      reason: row.reason || "",
    });
    resetMutationFeedback();
  };

  const handleDelete = async () => {
    if (!selectedStoreId || !deleteModal.targetDate) return;
    try {
      await deleteDayOverride({
        storeId: selectedStoreId,
        targetDate: deleteModal.targetDate,
      }).unwrap();
      setDeleteModal({ isOpen: false, targetDate: "" });
      setSuccessMessage("Day override removed.");
    } catch (err) {
      setDeleteModal({ isOpen: false, targetDate: "" });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTargetDate("");
    setForm({
      targetDate: today,
      effectiveDayOfWeek: "0",
      isActive: true,
      reason: "",
    });
    resetMutationFeedback();
  };

  const columns = [
    { key: "targetDate", label: "Target Date" },
    { key: "effectiveDay", label: "Effective Day" },
    { key: "status", label: "Status" },
    { key: "reason", label: "Reason" },
    { key: "updatedAt", label: "Updated At" },
    { key: "badge", label: "Rule Mapping" },
    { key: "actions", label: "Actions" },
  ];

  const rows = [...overrides]
    .sort((a, b) => String(b.targetDate || "").localeCompare(String(a.targetDate || "")))
    .map((entry) => {
      const targetDate = entry.targetDate || "-";
      const effectiveDayOfWeek = Number(entry.effectiveDayOfWeek ?? 0);
      const dayName = DAY_NAME_BY_VALUE[effectiveDayOfWeek] || `Day ${effectiveDayOfWeek}`;
      return {
        id: `${entry.storeId || selectedStoreId}-${targetDate}`,
        targetDate,
        effectiveDay: `${dayName} (${effectiveDayOfWeek})`,
        status: entry.isActive ? "Active" : "Inactive",
        reason: entry.reason || "-",
        updatedAt: entry.updatedAt
          ? new Date(entry.updatedAt).toLocaleString()
          : "-",
        badge: `Using ${dayName} rules for ${targetDate}`,
        actions: (
          <div className="flex items-center gap-3">
            <ActionButton
              label="Edit"
              icon="edit"
              onClick={() => handleEdit(entry)}
            />
            <ActionButton
              label="Delete"
              icon="delete"
              tone="danger"
              onClick={() =>
                setDeleteModal({ isOpen: true, targetDate: entry.targetDate })
              }
            />
          </div>
        ),
      };
    });

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Day Overrides"
          subtitle="Remap a date to another weekday's availability windows."
        />
        <Card>
          <Alert tone="warning">
            Please select a store to manage day overrides.
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Day Overrides"
        subtitle="Map a date to another weekday without changing weekly schedule definitions."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => {
                resetMutationFeedback();
                setShowForm(true);
              }}
            >
              Add Override
            </Button>
          )
        }
      />

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">
            {editingTargetDate ? "Edit Day Override" : "Add Day Override"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Target Date"
                name="targetDate"
                type="date"
                value={form.targetDate}
                onChange={handleChange}
                required
              />
              <Select
                label="Effective Day Of Week"
                name="effectiveDayOfWeek"
                value={form.effectiveDayOfWeek}
                onChange={handleChange}
                required
              >
                {DAY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Reason (Optional)"
                name="reason"
                placeholder="Special Sunday menu on Friday festival"
                value={form.reason}
                onChange={handleChange}
              />
              <Checkbox
                label="Active"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
                helper="Inactive overrides are ignored at runtime."
              />
            </div>

            {successMessage && <Alert tone="success">{successMessage}</Alert>}
            {upsertState.isError && (
              <Alert tone="error">
                {upsertState.error?.data?.detail ||
                  "Failed to save day override."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button type="submit" disabled={upsertState.isLoading}>
                {upsertState.isLoading ? "Saving..." : "Save Override"}
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
            Scheduling Overrides
          </div>
          <div className="px-6 py-4">
            <FilterBar
              startDate={draftFilters.from}
              endDate={draftFilters.to}
              onStartDateChange={(value) => {
                const next = {
                  ...draftFilters,
                  from: value ? toDateString(value) : "",
                };
                setFilterAndApply(next);
              }}
              onEndDateChange={(value) => {
                const next = {
                  ...draftFilters,
                  to: value ? toDateString(value) : "",
                };
                setFilterAndApply(next);
              }}
              showStore={false}
              onReset={() => {
                const next = { from: today, to: today };
                setFilterAndApply(next);
              }}
              isLoading={isFetching}
            />

            {successMessage && (
              <div className="mt-4">
                <Alert tone="success">{successMessage}</Alert>
              </div>
            )}
            {isError && (
              <div className="mt-4">
                <Alert tone="error">
                  {error?.data?.detail || "Failed to load day overrides."}
                </Alert>
              </div>
            )}

            <div className="mt-4">
              {isLoading ? (
                <p className="text-sm text-slate-500">Loading day overrides...</p>
              ) : (
                <Table
                  columns={columns}
                  rows={rows}
                  emptyText="No day overrides found for selected range."
                />
              )}
            </div>
          </div>
        </Card>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, targetDate: "" })}
        title="Delete Day Override"
        confirmText="Delete"
        onConfirm={handleDelete}
        isLoading={deleteState.isLoading}
      >
        <p>
          Are you sure you want to remove override for{" "}
          <strong>{deleteModal.targetDate}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default DayOverrides;
